<template>
  <div class="product-details">
    <div class="view-toggle-container" v-if="hasVariantData">
      <div class="toggle-label">View:</div>
      <div class="toggle-buttons">
        <button 
          :class="['toggle-btn', { active: viewMode === 'product' }]"
          @click="setViewMode('product')"
        >
          Product
        </button>
        <button 
          :class="['toggle-btn', { active: viewMode === 'variant' }]"
          @click="setViewMode('variant')"
        >
          Variant
        </button>
      </div>
    </div>

    <InlineEditor :value="displayedTitle" @input="onTitleEdit" :is-editable="isEditable" component-type="h1" />

    <div v-if="data.brandName">
      <p class="brand-name">
        <InlineEditor :value="data.brandName" :is-editable="isEditable" component-type="span" label="Brand Name" @input="onBrandNameEdit" />
      </p>
    </div>

    <div v-if="data.modelName">
      <p><InlineEditor :value="data.modelName" :is-editable="isEditable" component-type="span" label="Model Name" @input="onModelNameEdit" /></p>
    </div>

    <div><p>
      <InlineEditor :value="data.alliumCategory" :is-editable="isEditable" component-type="span" label="AlliumAI Category" @input="onAlliumCategoryEdit" />
    </p></div>

    <div class="gpc-field" v-if="data.gpcBrickId">
      <p>
        <strong>GPC Brick ID:</strong>
        <span>{{ data.gpcBrickId }}</span>
      </p>
    </div>

    <div
      class="gpc-field"
      v-if="data.gpcFullGpcTitle"
      :class="{ 'out-of-closed-list': highlightGpc }"
    >
      <p>
        <strong>GPC Category Path:</strong>
        <span>{{ data.gpcFullGpcTitle }}</span>
      </p>
    </div>

    <div
      class="gpc-field"
        v-if="data.rawGpcPath"
        :class="{ 'out-of-closed-list': highlightGpc }"
      >
      <p>
        <strong>GPC Category Path:</strong>
        <span>{{ data.rawGpcPath }}</span>
      </p>
    </div>

    <div class="web-category-section">
        <h2>Web Categories</h2>
        <div v-if="Array.isArray(data.webCategory) && data.webCategory.length">
            <ul>
                <li
                    v-for="(category, index) in data.webCategory"
                    :key="index"
                    class="web-category-item"
                >
                    <div class="category-content">
                        <InlineEditor :value="category" :is-editable="true" component-type="span" @input="(e) => onWebCategoryEdit(index, e)" />
                    </div>
                    <div class="category-actions">
                        <button class="delete-btn" @click="removeWebCategory(index)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2">
                                <path d="M18 6L6 18M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                </li>
            </ul>
        </div>

        <div v-else-if="typeof data.webCategory === 'string' && data.webCategory.length">
            <ul>
                <li class="web-category-item">
                    <div class="category-content">
                        <InlineEditor :value="data.webCategory" :is-editable="true" component-type="span" @input="(e) => onWebCategoryStringEdit(e)" />
                    </div>
                    <div class="category-actions">
                        <button class="delete-btn" @click="clearWebCategory">
                            </button>
                    </div>
                </li>
            </ul>
        </div>
        
        <div v-else>
            <p>No web categories available.</p>
        </div>
        <button v-if="isEditable" @click="addWebCategory" class="add-button">+ Add Category</button>
    </div>
    <div class="color-information">
      <div class="color-item">
        <InlineEditor :value="data.primaryColor" :is-editable="isEditable" component-type="span" label="Primary Color" @input="onPrimaryColorEdit" />
      <!-- </div> TODO: This closing tag breaks the build process so there must be an unbalanced tag somewhere else in the code? Needs fixing -->
      <div class="color-item">
        <InlineEditor :value="data.primaryColorHexCode" :is-editable="isEditable" component-type="span" label="Primary Color Hex Code" @input="onPrimaryColorHexCodeEdit" />
      </div>
    </div>

    <div class="color-item" v-if="data.supplierColor">
      <InlineEditor :value="data.supplierColor" :is-editable="isEditable" component-type="span" label="Supplier Color" @input="onSupplierColorEdit" />
    </div>

    <div class="color-item">
      <strong>All Colors:</strong>
      <span class="editable-array">
        <span
          v-for="(color, index) in data.allColors"
          :key="index"
          class="array-item"
        >
          <span
            :contenteditable="isEditable"
            @input="(e) => onAllColorsEdit(index, e)"
          >{{ color }}</span>
          <button v-if="isEditable" class="delete-item" @click="removeAllColor(index)">×</button>
        </span>
        <button v-if="false" class="add-item" @click="addAllColor">+</button>
      </span>
    </div>

    <div class="product-color" v-if="availableColors && availableColors.length">
      <label>Available Colors:</label>
      <div class="color-swatches">
        <div v-for="colorObj in availableColors" :key="colorObj.rawValue"
             class="color-swatch"
             :style="getSwatchStyle(colorObj.displayValue)"
             @click="filteredColors?.includes(colorObj.rawValue) ? selectColor(colorObj.rawValue) : ''"             
             :class="{ 
                'active-swatch': isColorActive(colorObj.rawValue),
                'disabled-swatch': !filteredColors?.includes(colorObj.rawValue) || undefined
             }">
          <span v-if="isColorActive(colorObj.rawValue)" class="swatch-selected">✓</span>
        </div>
      </div>
    </div>

    <div class="product-size" v-if="availableSizes && availableSizes.length">
      <label>Available Sizes:</label>
      <div class="size-options">
        <button
          v-for="(size, index) in availableSizes"
          :key="index"
          :class="{ selected: localSelectedSize === size }"
          @click="selectSize(size)"
          :disabled="!filteredSizes.includes(size)"

        >
          {{ size }}
        </button>
      </div>
    </div>

    <div class="product-size" v-if="allStyleSizes && allStyleSizes.length">
      <label>Available Style Sizes:</label>
      <div class="size-options">
        <span
          v-for="(styleSize, index) in allStyleSizes"
          :key="index"
          class="style-size-badge"
        >
          {{ styleSize }}
        </span>
      </div>
    </div>

    <!-- <div v-if="duplicateVariants.length > 0">
      <p v-for="(duplicate, i) in duplicateVariants">
        {{ duplicate }}
      </p>
    </div> -->
    
    <div v-if="duplicateVariants.length > 0">
      <strong style="color: red">Duplicate Product Variants:</strong>
      <v-data-table
        density="compact"
        items-per-page="-1"
        hide-default-footer="true"
        :headers="duplicateVariantHeaders"
        :items="duplicateVariants">
      </v-data-table>
    </div>

    <div class="product-bullet-points">
      <h2>Key Features</h2>
      <div v-if="displayedBulletPoints && displayedBulletPoints.length">
        <ul>
          <li
            v-for="(point, index) in displayedBulletPoints"
            :key="index"
            class="bullet-point-item"
          >
            <div class="bullet-content">
              <span
                :contenteditable="isEditable"
                @input="(e) => onBulletPointEdit(index, e)"
              >{{ point }}</span>
            </div>
            <div class="bullet-actions" v-if="isEditable">
              <button class="delete-btn" @click="removeBulletPoint(index)">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2">
                  <path d="M18 6L6 18M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </li>
        </ul>
        <button v-if="isEditable" @click="addBulletPoint" class="add-button">+ Add Feature</button>
      </div>
      <div v-else>
        <p>No key features available.</p>
        <button v-if="isEditable" @click="addBulletPoint" class="add-button">+ Add Feature</button>
      </div>
    </div>

    <div class="product-description">
      <h2>Description</h2>
      <div>
        <InlineEditor :value="displayedDescription" @input="updateDescription" :is-editable="isEditable" />
      </div>
    </div>

    <div
      class="product-specifications"
      v-if="data.specifications && Object.keys(data.specifications).length"
    >
      <div class="specifications-header">
        <h2>Product Specifications</h2>
        <button @click="addSpecification" class="add-button">+ Add Specification</button>
      </div>

      <table>
        <tbody>
        <tr v-for="(value, key) in data.specifications" :key="key">
          <td class="spec-key">
            <span :contenteditable="isEditable" @input="(e) => onSpecKeyEdit(key, e)">{{ key }}</span>
          </td>
          <td class="spec-value">
            <span
              :contenteditable="isEditable"
              @input="(e) => onSpecificationValueEdit(key, e)"
              class="editable-spec"
            >
              {{ value }}
            </span>
          </td>
          <td class="spec-actions" v-if="isEditable">
            <button class="delete-btn" @click="deleteSpecification(key)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"></path>
              </svg>
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div
      class="product-specifications"
      v-if="data.materialDeclaration && data.materialDeclaration.length"
    >
      <div class="specifications-header">
        <h2>Material Declaration</h2>
      </div>
      <table>
        <thead>
        <tr>
          <th>Type</th>
          <th>ID</th>
          <th>Material Name</th>
          <th>Percent</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(mat, index) in data.materialDeclaration"
          :key="index"
        >
          <td class="spec-key">
            {{ mat.type }}
          </td>
          <td class="spec-key">
            {{ mat.id }}
          </td>
          <td class="spec-value">
            {{ mat.name?.[materialLang] || 'Unknown' }}
          </td>
          <td class="spec-value">
            {{ mat.percent != null ? mat.percent + '%' : '' }}
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="stibo-material-container" v-if="materialContainerItems">
      <h2>Stibo Material Container</h2>
      <v-data-table
        density="compact"
        items-per-page="-1"
        hide-default-footer="true"
        :headers="materialContainerHeaders"
        :items="materialContainerItems">
      </v-data-table>
    </div>

    <div v-if="showAddSpecModal" class="modal-overlay">
      <div class="modal-content">
        <h3>Add New Specification</h3>
        <div class="form-group">
          <label for="spec-name">Specification Name:</label>
          <input
            type="text"
            id="spec-name"
            v-model="newSpec.key"
            placeholder="Enter specification name"
          />
        </div>
        <div class="form-group">
          <label for="spec-value">Specification Value:</label>
          <input
            type="text"
            id="spec-value"
            v-model="newSpec.value"
            placeholder="Enter specification value"
          />
        </div>
        <div class="modal-buttons">
          <button class="cancel-btn" @click="showAddSpecModal = false">Cancel</button>
          <button class="add-btn" @click="confirmAddSpecification" :disabled="!canAddSpec">Add</button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import InlineEditor from './InlineEditor.vue';
import { normalizeHexColor } from '@/productviewerutils'

export default {
  name: 'ProductDetails',
  props: {
    isEditable: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        title: 'No title available',
        description: 'No description available.',
        modelName: '',
        alliumCategory: '',
        gpcBrickId: '',
        gpcFullGpcTitle: '',
        webCategoryCode: [],
        brandName: '',
        supplierColor: '',
        specifications: {},
        primaryColorHexCode: null,
        primaryColor: 'Unknown',
        allColors: [],
        bulletPoints: [],
        materialDeclaration: []
      }),
    },
    highlightGpc: {
      type: Boolean,
      default: false
    },    
    selectedColor: {
      type: String,
      default: '',
    },
    selectedSize: {
      type: String,
      default: '',
    },
    isAfter: {
      type: Boolean,
      default: false,
    },
    editedDetails: {
      type: Object,
      default: () => ({}),
    },
    selectedLanguage: {
      type: String,
      default: 'english'
    },
    productVariants: {
      type: Array,
      default: () => [],
    },
    variantData: {
      type: Object,
      default: () => ({
        title: null,
        description: null,
        bulletPoints: []
      }),
    },
    hasVariantData: {
      type: Boolean,
      default: false,
    },
    currentVariantId: {
      type: String,
      default: '',
    }
  },
  components: {
    InlineEditor
  },
  data() {
    return {
      localSelectedColor: this.selectedColor,
      localSelectedSize: this.selectedSize,
      showAddSpecModal: false,
      newSpec: { key: '', value: '' },
      viewMode: this.hasVariantData ? 'variant' : 'product',
    };
  },
  computed: {
    displayedTitle() {
      if (this.viewMode === 'variant' && this.variantData.title) {
        return this.variantData.title;
      }
      return this.data.title;
    },
    displayedDescription() {
      if (this.viewMode === 'variant' && this.variantData.description) {
        return this.variantData.description;
      }
      return this.data.description;
    },
    displayedBulletPoints() {
      if (this.viewMode === 'variant' && this.variantData.bulletPoints && this.variantData.bulletPoints.length > 0) {
        return this.variantData.bulletPoints;
      }
      return this.data.bulletPoints || [];
    },
    allColors() {
      const setOfColors = new Map();
      for (const v of this.productVariants) {
        const cData = v.variantDiscriminators?.color || {};
        let rawVal = cData.rgbColor || cData.colorCode;
        if (!rawVal && Array.isArray(cData.attrValues) && cData.attrValues[0]) {
          rawVal = cData.attrValues[0].value;
        }
        if (!rawVal) continue;
        const displayVal = normalizeHexColor(rawVal) || rawVal;
        setOfColors.set(rawVal, { rawValue: rawVal, displayValue: displayVal });
      }
      return Array.from(setOfColors.values());
    },
    duplicateVariants() {
      const discriminator_func = (productVariant => {
        const v = productVariant.variantDiscriminators || {};
        return `${v.color?.colorCode}::${v.size?.size}::${v.size?.styleSize}`;
      });

      const duplicates = this.productVariants.filter(current => {
        return this.productVariants.filter(
          other => discriminator_func(current) === discriminator_func(other)
        ).length > 1;
      });

      duplicates.sort((a, b) => {
        return discriminator_func(a).localeCompare(discriminator_func(b));
      });

      return Object.entries(Object.groupBy(duplicates, (o) => {
        return discriminator_func(o);
      })).map(tuple => tuple[1]);
    },
    availableColors() {
      const setOfColors = new Map();
      for (const v of this.productVariants) {
        const cData = v.variantDiscriminators?.color || {};
        let rawVal = cData.rgbColor || cData.colorCode;
        if (!rawVal && Array.isArray(cData.attrValues) && cData.attrValues[0]) {
          rawVal = cData.attrValues[0].value;
        }
        if (!rawVal) continue;
        const displayVal = normalizeHexColor(rawVal) || rawVal;
        setOfColors.set(rawVal, { rawValue: rawVal, displayValue: displayVal });
      }
      return Array.from(setOfColors.values());
    },

    // Get all unique sizes from the valid combinations
    allSizes() {
      const setOfSizes = new Set();
      for (const v of this.productVariants) {
        const sz = v.variantDiscriminators?.size?.size;
        if (sz) setOfSizes.add(sz);
      }
      return Array.from(setOfSizes);
    },
    
    availableSizes() {
      const setOfSizes = new Set();
      for (const v of this.productVariants) {
        const sz = v.variantDiscriminators?.size?.size;
        if (sz) setOfSizes.add(sz);
      }
      return Array.from(setOfSizes);
    },

    // Get all unique style sizes from the valid combinations
    allStyleSizes() {
      const setOfStyleSizes = new Set();
      for (const v of this.productVariants) {
        const styleSz = v.variantDiscriminators?.size?.styleSize;
        if (styleSz) setOfStyleSizes.add(styleSz);
      }
      return Array.from(setOfStyleSizes);
    },

    // Filter available sizes based on the selected color
    filteredSizes() {
      if (!this.localSelectedColor) {
        return this.allSizes; // If no color is selected, all sizes are available
      }
      const available = new Set();
      this.productVariants.forEach(v => {
        // Get the color's unique raw value for this variant
        const colorData = v.variantDiscriminators?.color || {};
        let variantColor = colorData.rgbColor || colorData.colorCode;
        if (!variantColor && Array.isArray(colorData.attrValues) && colorData.attrValues[0]) {
          variantColor = colorData.attrValues[0].value;
        }

        // If the variant's color matches the selected color, add its size to our set
        if (variantColor === this.localSelectedColor) {
          const variantSize = v.variantDiscriminators?.size?.size;
          if (variantSize) {
            available.add(variantSize);
          }
        }
      });
      return Array.from(available);
    },

    // Filter available colors based on the selected size
    filteredColors() {
      if (!this.localSelectedSize) {
        return this.allColors.map(c => c.rawValue); // If no size is selected, all colors are available
      }
      const available = new Set();
      this.productVariants.forEach(v => {
        // Get the size for this variant
        const variantSize = v.variantDiscriminators?.size?.size;

        // If the variant's size matches the selected size, add its color to our set
        if (variantSize === this.localSelectedSize) {
          const colorData = v.variantDiscriminators?.color || {};
          let variantColor = colorData.rgbColor || colorData.colorCode;
          if (!variantColor && Array.isArray(colorData.attrValues) && colorData.attrValues[0]) {
            variantColor = colorData.attrValues[0].value;
          }
          if (variantColor) {
            available.add(variantColor);
          }
        }
      });
      return Array.from(available);
    },
    canAddSpec() {
      return this.newSpec.key.trim() !== '' && this.newSpec.value.trim() !== '';
    },
    // compute which material name key we want, e.g. 'EN', 'DE', 'FR', 'IT'
    materialLang() {
      switch (this.selectedLanguage) {
        case 'german':
          return 'DE';
        case 'french':
          return 'FR';
        case 'italian':
          return 'IT';
        default:
          return 'EN';
      }
    },
    materialContainerItems() {
      const rawItems = this.data?.alliumInternalData?.materialContainer?.entries;
      const formatCellData = (o) => o?.attrValues?.map(v => v.valueLabels[this.materialLang]).join(", ") || "";

      return rawItems?.map(entry => Object.fromEntries(
        Object.entries(entry).map(([key, value]) => [key, formatCellData(value)]))
      );
    },
    materialContainerHeaders() { // Not actually computed but it's here to be colocated with materialContainerItems :)
      return [
        { title: "Type", value: "componentType" },
        { title: "Name", value: "name" },
        { title: "Percentage", value: "percentage" },
        { title: "Construction", value: "construction" },
        { title: "Hero Name", value: "heroName" },
      ];
    },
    duplicateVariantHeaders() {
      return [
        {title: "Size", key: "size", value: (v) => v[0].variantDiscriminators?.size?.size || "None" },
        {title: "Style Size", key: "styleSize", value: (v) => v[0].variantDiscriminators?.size?.styleSize || "None" },
        {title: "Color", key: "color", value: (v) => v[0].variantDiscriminators?.color?.colorCode || "None" },
        {title: "IDs", key: "id_count", value: (v) => v.map(o => o.id).join(", ") },
      ];
    },
    webCategoriesList() {
      if (Array.isArray(this.data.webCategory)) {
        return this.data.webCategory;
      }
      return [];
    }
  },
  watch: {
    localSelectedColor(newColor) {
      this.$emit('colorSelected', newColor);
    },
    localSelectedSize(newSize) {
      this.$emit('sizeSelected', newSize);
    },
    selectedColor(newColor) {
      this.localSelectedColor = newColor;
    },
    selectedSize(newSize) {
      this.localSelectedSize = newSize;
    },
  },
  methods: {
    setViewMode(mode) {
      this.viewMode = mode;
    },
    updateDescription(event) {
      if (event?.target?.innerHTML)
      {
        this.$emit('detailEdited', { 
          field: 'description', 
          value: event.target.innerHTML,
          isVariantLevel: this.viewMode === 'variant',
          variantId: this.currentVariantId
        });
      }
    },
    updateBulletPoints(newValue) {
      
        this.$emit('detailEdited', { field: 'bulletPoints', newValue });
      
    },
    selectColor(colorValue) {
      // If the clicked color is already selected, deselect it. Otherwise, select it.
      this.localSelectedColor = this.localSelectedColor === colorValue ? '' : colorValue;
      this.$emit('colorSelected', this.localSelectedColor);
    },
    isColorActive(colorValue) {
      return this.localSelectedColor === colorValue;
    },
    selectSize(size) {
      // If the clicked size is already selected, deselect it. Otherwise, select it.
      this.localSelectedSize = this.localSelectedSize === size ? '' : size;
      this.$emit('sizeSelected', this.localSelectedSize);
    },
    getSwatchStyle(colorValue) {
      return { backgroundColor: colorValue || '#ccc' };
    },

    // Title
    onTitleEdit(event) {
      if (!event.target) return;
      const newTitle = event.target.innerText.trim();
      this.$emit('detailEdited', { 
        field: 'title', 
        value: newTitle,
        isVariantLevel: this.viewMode === 'variant',
        variantId: this.currentVariantId
      });
    },
    onBrandNameEdit(event) {
      if (!event.target) return;
      const newBrandName = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'brandName', value: newBrandName });
    },
    onModelNameEdit(event) {
      if (!event.target) return;
      const val = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'modelName', value: val });
    },
    onAlliumCategoryEdit(event) {
      if (!event.target) return;
      const val = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'alliumCategory', value: val });
    },
    
    // Web Category Methods
    onWebCategoryEdit(index, event) {
        if (!event.target) return;
        const newCategory = event.target.innerText.trim();
        const categories = [...(this.data.webCategory || [])];
        categories[index] = newCategory;
        this.$emit('detailEdited', { field: 'webCategory', value: categories });
    },
    addWebCategory() {
        const categories = [...(this.data.webCategory || []), 'New Category > Path'];
        this.$emit('detailEdited', { field: 'webCategory', value: categories });
    },
    removeWebCategory(index) {
        const categories = [...(this.data.webCategory || [])];
        categories.splice(index, 1);
        this.$emit('detailEdited', { field: 'webCategory', value: categories });
    },

    // Colors
    onPrimaryColorEdit(event) {
      if (!event.target) return;
      const newVal = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'primaryColor', value: newVal });
    },
    onPrimaryColorHexCodeEdit(event) {
      if (!event.target) return;
      const newVal = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'primaryColorHexCode', value: newVal });
    },
    onSupplierColorEdit(event) {
      if (!event.target) return;
      const newVal = event.target.innerText.trim();
      this.$emit('detailEdited', { field: 'supplierColor', value: newVal });
    },
    
    // All Colors
    onAllColorsEdit(index, event) {
      const newColor = event.target.innerText.trim();
      const colors = [...(this.data.allColors || [])];
      colors[index] = newColor;
      this.$emit('detailEdited', { field: 'allColors', value: colors });
    },
    addAllColor() {
      const colors = [...(this.data.allColors || []), 'New Color'];
      this.$emit('detailEdited', { field: 'allColors', value: colors });
    },
    removeAllColor(index) {
      const colors = [...(this.data.allColors || [])];
      colors.splice(index, 1);
      this.$emit('detailEdited', { field: 'allColors', value: colors });
    },

    // Bullet Points
    onBulletPointEdit(index, event) {
      const newText = event.target.innerText.trim();
      const bulletPoints = [...this.displayedBulletPoints];
      bulletPoints[index] = newText;
      alert("EEEE!!");
      this.$emit('detailEdited', { 
        field: 'bulletPoints', 
        value: bulletPoints,
        isVariantLevel: this.viewMode === 'variant',
        variantId: this.currentVariantId
      });
    },
    addBulletPoint() {
      const bulletPoints = [...this.displayedBulletPoints, 'New feature'];
      this.$emit('detailEdited', { 
        field: 'bulletPoints', 
        value: bulletPoints,
        isVariantLevel: this.viewMode === 'variant',
        variantId: this.currentVariantId
      });
    },
    removeBulletPoint(index) {
      const bulletPoints = [...this.displayedBulletPoints];
      bulletPoints.splice(index, 1);
      this.$emit('detailEdited', { 
        field: 'bulletPoints', 
        value: bulletPoints,
        isVariantLevel: this.viewMode === 'variant',
        variantId: this.currentVariantId
      });
    },

    // Description
    onDescriptionEdit(event) {
      const newDescription = event.target.innerHTML;
      this.$emit('detailEdited', { field: 'description', value: newDescription });
    },

    // Specifications
    onSpecKeyEdit(oldKey, event) {
      const newKey = event.target.innerText.trim();
      if (oldKey === newKey) return;
      const specs = { ...this.data.specifications };
      const val = specs[oldKey];
      delete specs[oldKey];
      specs[newKey] = val;
      this.$emit('detailEdited', { field: 'specifications', value: specs });
    },
    onSpecificationValueEdit(key, event) {
      const newValue = event.target.innerText.trim();
      const specs = { ...this.data.specifications };
      specs[key] = newValue;
      this.$emit('detailEdited', { field: 'specifications', value: specs });
    },
    deleteSpecification(key) {
      const specs = { ...this.data.specifications };
      delete specs[key];
      this.$emit('detailEdited', { field: 'specifications', value: specs });
    },
    addSpecification() {
      this.showAddSpecModal = true;
    },
    confirmAddSpecification() {
      if (!this.canAddSpec) return;
      const specs = { ...this.data.specifications };
      specs[this.newSpec.key] = this.newSpec.value;
      this.$emit('detailEdited', { field: 'specifications', value: specs });
      this.newSpec.key = '';
      this.newSpec.value = '';
      this.showAddSpecModal = false;
    },
  },
};
</script>

<style scoped>
.product-details {
}

/* View Toggle Styles */
.view-toggle-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.toggle-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.toggle-buttons {
  display: flex;
  gap: 0;
  border: 2px solid #2c3e50;
  border-radius: 6px;
  overflow: hidden;
}

.toggle-btn {
  padding: 8px 20px;
  border: none;
  background-color: white;
  color: #2c3e50;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.toggle-btn:first-child {
  border-right: 1px solid #2c3e50;
}

.toggle-btn:hover:not(.active) {
  background-color: #e9ecef;
}

.toggle-btn.active {
  background-color: #2c3e50;
  color: white;
}

.editable-field {
  position: relative;
  border: 1px solid transparent;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  margin-bottom: 15px;
}
.editable-field:hover {
  border-color: #dee2e6;
}
.edit-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #6c757d;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s;
}
.editable-field:hover .edit-indicator {
  opacity: 1;
}

.product-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #2c3e50;
  outline: none;
}

.brand-name,
.color-information {
  font-size: 16px;
}

.color-item {
  margin-bottom: 10px;
}

.brand-name span,
.color-item span {
  outline: none;
}

.editable-array {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.array-item {
  display: inline-flex;
  align-items: center;
  background-color: #f0f4f8;
  border-radius: 4px;
  padding: 4px 8px;
}

.array-item span {
  outline: none;
  min-width: 40px;
}

.delete-item {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
  padding: 0 4px;
}

.add-item {
  background-color: #2c3e50;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.product-color {
  margin-bottom: 20px;
}

.color-swatches {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
.color-swatch {
  width: 40px;
  height: 40px;
  border: 2px solid #e0e0e0;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s, border-color 0.2s;
  box-sizing: border-box;
}

.disabled-swatch {
  /* Fades the swatch to 50% to show it's inactive */
  opacity: 0.5;

  /* Draws a semi-transparent grey line diagonally over the swatch */
  background-image: linear-gradient(
    to top right,
    transparent 48.5%,
    rgba(128, 128, 128, 0.7) 49.5%,
    rgba(128, 128, 128, 0.7) 50.5%,
    transparent 51.5%
  );

  /* Shows a "not allowed" icon on hover */
  cursor: not-allowed;
}

/* Optional but recommended: disable any hover effects on the swatch */
.disabled-swatch:hover {
  transform: none;
  border-color: #e0e0e0; /* Prevents border from changing color on hover */
}

.color-swatch:hover {
  transform: scale(1.1);
  border-color: #2c3e50;
}

.active-swatch {
  border-color: #2c3e50 !important;
  border-width: 2px !important;
  transform: scale(1.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
.swatch-selected {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #2c3e50;
  color: #fff;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
}

.product-size {
  margin-bottom: 20px;
}
.size-options button {
  margin-right: 10px;
  padding: 8px 12px;
  border: 1px solid #2c3e50;
  background-color: #fff;
  color: #2c3e50;
  cursor: pointer;
  transition: background-color 0.3s;
}
.size-options button.selected,
.size-options button:hover {
  background-color: #2c3e50;
  color: #fff;
}

.style-size-badge {
  display: inline-block;
  margin-right: 10px;
  padding: 8px 12px;
  border: 1px solid #6c757d;
  background-color: #f8f9fa;
  color: #495057;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
}

.product-bullet-points,
.web-category-section {
  margin-bottom: 30px;
}
.product-bullet-points h2,
.product-description h2,
.product-specifications h2,
.web-category-section h2 {
  font-size: 22px;
  margin-bottom: 15px;
  color: #2c3e50;
  font-weight: 600;
}
.product-bullet-points ul,
.web-category-section ul {
  list-style-type: disc;
  margin-left: 20px;
  margin-bottom: 15px;
}
.web-category-section ul {
  list-style-type: none;
  margin-left: 0;
  padding-left: 0;
}

.bullet-point-item,
.web-category-item {
  display: flex;
  align-items: center; /* Use center for better alignment with buttons */
  margin-bottom: 8px;
}

.bullet-content,
.category-content {
  flex: 1;
  outline: none;
}
.category-content {
    margin: -4px; /* Counteract editable-field padding */
    margin-right: 8px;
}
.bullet-actions,
.category-actions {
  opacity: 0;
  transition: opacity 0.2s;
}
.bullet-point-item:hover .bullet-actions,
.web-category-item:hover .category-actions {
  opacity: 1;
}
.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.delete-btn:hover {
  color: #b02a37;
}
.add-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.add-button:hover {
  background-color: #34495e;
}
.editable-description {
  outline: none;
  line-height: 1.6;
}

.specifications-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.product-specifications table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
}
.product-specifications th,
.product-specifications td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.spec-key {
  width: 30%;
  font-weight: 600;
  background-color: #f8f9fa;
}
.spec-key span {
  outline: none;
}
.spec-value {
  outline: none;
}
.spec-value span {
  outline: none;
}
.spec-actions {
  width: 50px;
  text-align: center;
}
.editable-spec {
  outline: none;
  min-width: 50px;
  display: inline-block;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}
.modal-content h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 20px;
}
.form-group {
  margin-bottom: 16px;
}
.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}
.form-group input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 16px;
}
.form-group input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
}
.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}
.modal-buttons button {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}
.cancel-btn {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  color: #212529;
}
.add-btn {
  background-color: #2c3e50;
  border: 1px solid #2c3e50;
  color: #fff;
}
.add-btn:hover:not(:disabled) {
  background-color: #34495e;
}
.add-btn:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}
.out-of-closed-list {
  background-color: #FFF1C1; /* or whatever "Out of Closed List" color you use */
  transition: background-color 0.3s;
  padding: 8px;
  border-radius: 4px;
}
.size-options button:disabled {
  color: #999; /* Changes text color to grey */
  text-decoration: line-through; /* Adds the horizontal line */
  cursor: not-allowed; /* Shows a "not allowed" cursor on hover */
  background-color: #f5f5f5; /* Optional: lighter background for disabled state */
}
.stibo-material-container :deep(tbody tr:nth-of-type(odd)) {
  background-color: #f9f9f9; /* Add subtle zebra striping to odd rows */
}
.stibo-material-container :deep(tbody tr:hover) {
  background-color: #eaeaea; /* Add a darker highlight when data row hovered */
}
</style>